import { produce } from 'immer';
import {
    LabelAction,
    LabelCreateAction,
    LabelDeleteAction,
    LabelsLoadReplaceAction,
    LabelsSetFullFetchTimestampAction,
    LabelRecreateAction,
    LabelsLoadMergeAction,
    LabelUpdateAction,
    LabelSelectAction,
    LabelState,
    LabelStartRenameAction,
} from '../../../common/labels/labelTypes';
import * as LABELS_ACTIONS from '../../../common/labels/labelConstants';
import * as LABELS_ACTION_TYPE from '../../../common/labels/labelConstants';

const initialState: LabelState = {
    currentUserLabels: {},
    fullLabelFetchTimestamp: null,
    staleTimestamp: null,
    selectedLabel: null,
    editingLabelId: null,
};

const labelsReducer = (state = initialState, action: LabelAction): LabelState => {
    switch (action.type) {
        case LABELS_ACTIONS.LABELS_LOAD_MERGE:
            return produce(state, (draft) => {
                const { labels } = action as LabelsLoadMergeAction;

                labels.forEach((label) => {
                    draft.currentUserLabels[label._id] = label;
                });
            });

        case LABELS_ACTIONS.LABELS_LOAD_REPLACE:
            return produce(state, (draft) => {
                const nextLabels = (action as LabelsLoadReplaceAction).labels.reduce(
                    (acc, label) => {
                        acc[label._id] = label;
                        return acc;
                    },
                    {} as LabelState['currentUserLabels'],
                );

                draft.currentUserLabels = nextLabels;
                draft.staleTimestamp = null;
            });

        case LABELS_ACTIONS.LABELS_SET_FULL_FETCH_TIMESTAMP:
            return produce(state, (draft) => {
                draft.fullLabelFetchTimestamp = (action as LabelsSetFullFetchTimestampAction).timestamp;
            });

        case LABELS_ACTION_TYPE.LABEL_CREATE:
        case LABELS_ACTION_TYPE.LABEL_RECREATE:
            return produce(state, (draft) => {
                const { label } = action as LabelCreateAction | LabelRecreateAction;
                draft.currentUserLabels[label._id] = { ...label, readOnly: false };
            });

        case LABELS_ACTIONS.LABEL_UPDATE:
            return produce(state, (draft) => {
                const { labelId, changes } = action as LabelUpdateAction;
                const existingLabel = draft.currentUserLabels[labelId];
                if (!existingLabel) return;

                draft.currentUserLabels[labelId] = { ...existingLabel, ...changes };
            });

        case LABELS_ACTIONS.LABEL_DELETE:
            return produce(state, (draft) => {
                const { labelId, remote, deleted } = action as LabelDeleteAction;
                if (remote && !deleted) return;

                delete draft.currentUserLabels[labelId];
            });

        case LABELS_ACTIONS.LABEL_SELECT:
            return produce(state, (draft) => {
                const { selectedLabel } = action as LabelSelectAction;
                draft.selectedLabel = selectedLabel;
                draft.editingLabelId = null;
            });

        case LABELS_ACTIONS.LABEL_RENAME_START:
            return produce(state, (draft) => {
                const labelId = (action as LabelStartRenameAction).labelId;
                draft.editingLabelId = labelId;
            });

        case LABELS_ACTIONS.LABEL_RENAME_FINISH: {
            return produce(state, (draft) => {
                draft.editingLabelId = null;
            });
        }

        case LABELS_ACTIONS.LABELS_MARK_STALE:
            return produce(state, (draft) => {
                draft.staleTimestamp = Date.now();
            });

        default:
            return state;
    }
};

export default labelsReducer;
